import type { ProjectNodeData } from "@/types/project"
import type { NodeParameter } from "./parameter-utils"

export type NodeHandleTone =
	| "neutral"
	| "muted"
	| "accent"
	| "success"
	| "warning"
	| "danger"

export type NodeValueRailConfig = {
	minParam: NodeParameter
	valueParam: NodeParameter
	maxParam: NodeParameter
}

const PRIORITY_VALUE_NAMES = ["value", "current", "count", "steps"] as const

function isFiniteNumber(value: unknown): value is number {
	return typeof value === "number" && Number.isFinite(value)
}

function hasPresentValue(value: unknown) {
	if (value === null || value === undefined) return false
	if (typeof value === "string") return value.trim().length > 0
	return true
}

export function getNodeValueRailConfig(
	label: string,
	parameters: NodeParameter[],
): NodeValueRailConfig | null {
	const parameterByName = new Map(
		parameters.map((parameter) => [parameter.name, parameter]),
	)
	const minParam = parameterByName.get("min")
	const maxParam = parameterByName.get("max")

	if (!minParam || !maxParam) return null
	if (!isFiniteNumber(minParam.value) || !isFiniteNumber(maxParam.value))
		return null

	let valueParam = PRIORITY_VALUE_NAMES.map((name) =>
		parameterByName.get(name),
	).find((parameter) => parameter && isFiniteNumber(parameter.value))

	if (!valueParam) {
		const numericOthers = parameters.filter(
			(parameter) =>
				parameter.name !== "min" &&
				parameter.name !== "max" &&
				isFiniteNumber(parameter.value),
		)

		if (numericOthers.length === 1) {
			valueParam = numericOthers[0]
		}
	}

	const normalizedLabel = label.toLowerCase()
	const looksLikeSlider =
		normalizedLabel.includes("slider") ||
		(parameters.length <= 3 && valueParam !== undefined)

	if (!looksLikeSlider || !valueParam) return null

	return {
		minParam,
		valueParam,
		maxParam,
	}
}

export function getParameterHandleTone(
	parameter: NodeParameter,
	isConnected: boolean,
): NodeHandleTone {
	if (isConnected) return "accent"
	if (parameter.required && !hasPresentValue(parameter.value)) return "muted"
	return "neutral"
}

export function getOutputHandleTone(
	status: ProjectNodeData["status"],
): NodeHandleTone {
	switch (status) {
		case "completed":
			return "success"
		case "running":
			return "warning"
		case "failed":
			return "danger"
		default:
			return "neutral"
	}
}
